/* eslint-disable react/prop-types */
/**
 * LinkGoalsDialog — extracted VERBATIM from App.js (Dashboard),
 * V2 Notes migration Phase 3B-2 Step 2 (Jun 2026). Pure mechanical
 * lift; all state lives in the Dashboard and flows through ctx.
 */
import { toast } from "sonner";
import { FileText, Loader, Target } from "lucide-react";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";

export const LinkGoalsDialog = ({ ctx }) => {
  const {
    availableTreatmentPlanGoals,
    linkedGoals,
    loadingTreatmentPlanGoals,
    setLinkedGoals,
    setSelectedNote,
    setShowLinkGoalsDialog,
    showLinkGoalsDialog,
  } = ctx;

  return (
      <Dialog open={showLinkGoalsDialog} onOpenChange={setShowLinkGoalsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              Link Treatment Plan Goals
            </DialogTitle>
            <DialogDescription>
              Select goals from active treatment plans to link to this note. Linked goals will be referenced in the note content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {loadingTreatmentPlanGoals ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-purple-600" />
                <span className="ml-2 text-gray-600">Loading treatment plan goals...</span>
              </div>
            ) : availableTreatmentPlanGoals.length === 0 ? (
              <div className="text-center py-8">
                <Target className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No active treatment plan goals found for this client.</p>
                <p className="text-sm text-gray-500 mt-1">Goals must be part of an active treatment plan to be linked.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Select goals to link to this note. You can link multiple goals from different treatment plans.
                </div>

                {availableTreatmentPlanGoals.map((plan) => (
                  <div key={plan.treatment_plan_id} className="border rounded-lg overflow-hidden">
                    {/* Plan Header */}
                    <div className="bg-gray-50 px-4 py-2 border-b">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-800">{plan.service_type}</span>
                        <span className="text-xs text-gray-500 capitalize">({plan.status})</span>
                      </div>
                      {plan.provider_name && (
                        <p className="text-xs text-purple-600 mt-1">Developed by: {plan.provider_name}</p>
                      )}
                    </div>

                    {/* Goals within this plan */}
                    <div className="divide-y">
                      {plan.goals.map((goal, gIdx) => {
                        const isLinked = linkedGoals.some(lg => lg.goal_id === goal.goal_id);

                        return (
                          <div
                            key={goal.goal_id}
                            className={`p-4 cursor-pointer transition-colors ${
                              isLinked
                                ? 'bg-purple-50'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => {
                              if (isLinked) {
                                const updated = linkedGoals.filter(lg => lg.goal_id !== goal.goal_id);
                                setLinkedGoals(updated);
                                setSelectedNote(prev => prev ? {...prev, linked_goals: updated} : prev);
                              } else {
                                const newGoal = {
                                  ...goal,
                                  service_type: plan.service_type,
                                  treatment_plan_id: plan.treatment_plan_id,
                                  provider_name: plan.provider_name  // Include provider who developed the plan
                                };
                                const updated = [...linkedGoals, newGoal];
                                setLinkedGoals(updated);
                                setSelectedNote(prev => prev ? {...prev, linked_goals: updated} : prev);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={isLinked}
                                onChange={() => {}} // Handled by parent onClick
                                className="pointer-events-none mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {goal.pan_name && (
                                    <span className="text-xs text-purple-600 font-medium">PAN #{goal.pan_number || (gIdx + 1)}: {goal.pan_name}</span>
                                  )}
                                  {goal.target_end_date && (
                                    <span className="text-xs text-gray-400">
                                      Target: {new Date(goal.target_end_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>

                                <p className="text-sm font-medium text-gray-800 mb-2">
                                  <span className="text-purple-800">Goal #{gIdx + 1}:</span> {goal.goal_text}
                                </p>

                                {goal.goal_in_clients_words && (
                                  <p className="text-xs text-gray-600 mb-2 italic">
                                    <span className="font-medium not-italic">Client's words:</span> "{goal.goal_in_clients_words}"
                                  </p>
                                )}

                                {goal.objectives && goal.objectives.length > 0 && (
                                  <div className="pl-3 border-l-2 border-purple-200 mt-2 space-y-2">
                                    {goal.objectives.map((obj, objIdx) => (
                                      <div key={obj.objective_id} className="text-xs text-gray-600">
                                        <span className="font-bold text-blue-600">Objective #{objIdx + 1}:</span> {obj.objective_text}
                                        {obj.responsible_party && (
                                          <span className="text-gray-400 ml-1">({obj.responsible_party})</span>
                                        )}
                                        {obj.interventions?.length > 0 && (
                                          <div className="mt-1 pl-3 space-y-0.5">
                                            {obj.interventions.map((intervention, intIdx) => (
                                              <div key={intIdx} className="text-gray-500 flex items-baseline gap-1 flex-wrap">
                                                <span className="text-teal-600 font-medium">Intervention #{intIdx + 1}:</span>
                                                <span className="font-medium text-teal-700">{typeof intervention === 'string' ? intervention : intervention.name}</span>
                                                {typeof intervention === 'object' && intervention.description && (
                                                  <span className="text-gray-500">: {intervention.description}</span>
                                                )}
                                                {typeof intervention === 'object' && intervention.modality && (
                                                  <span className="text-gray-400">Modality: {intervention.modality}</span>
                                                )}
                                                {typeof intervention === 'object' && intervention.frequency && (
                                                  <span className="text-gray-400">Frequency: {intervention.frequency}</span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {!obj.interventions?.length && obj.intervention && (
                                          <div className="text-gray-400 ml-1">• {obj.intervention}</div>
                                        )}
                                        {obj.treatment_description && (
                                          <div className="text-gray-400 mt-0.5">
                                            <span className="font-medium text-gray-500">Treatment Details:</span> {obj.treatment_description}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-600">
              {linkedGoals.length} goal{linkedGoals.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowLinkGoalsDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  setShowLinkGoalsDialog(false);
                  toast.success(`${linkedGoals.length} goal${linkedGoals.length !== 1 ? 's' : ''} linked to note`);
                }}
              >
                Link Selected Goals
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
};
